import { MaterialCommunityIcons } from "@expo/vector-icons";
import { StyleSheet, Text, View } from "react-native";

import { colors, fonts, radius, spacing } from "@/lib/theme";

/**
 * OrientationCard — the read-only RN briefing for a Special / Ad-Hoc visit (P5).
 *
 * Rendered only for `formSource === "special"` visits, at the top of the assessment. The
 * `orientation` text is plain text but may carry light structure via newlines: a lead line, a
 * `Label:` sub-header, and `•`/`-` bullets. We render those with real hierarchy so the briefing
 * scans quickly instead of reading as one dense block. The agency's raw intake (`source_text`)
 * is never sent to the device, so it can't appear here.
 */

type Block =
    | { kind: "subhead"; text: string }
    | { kind: "bullet"; text: string }
    | { kind: "para"; text: string };

function parse(orientation: string): Block[] {
    return orientation
        .split(/\r?\n/)
        .map((l) => l.trim())
        .filter((l) => l.length > 0)
        .map((line): Block => {
            const bullet = line.match(/^[•\-*]\s+(.*)$/);
            if (bullet) {
                return { kind: "bullet", text: bullet[1] };
            }
            if (line.endsWith(":") && line.length <= 42) {
                return { kind: "subhead", text: line.replace(/:$/, "") };
            }
            return { kind: "para", text: line };
        });
}

export function OrientationCard({ orientation }: { orientation: string }) {
    const blocks = parse(orientation);

    return (
        <View style={styles.card}>
            <View style={styles.header}>
                <MaterialCommunityIcons
                    name="clipboard-text-outline"
                    size={15}
                    color={colors.teal600}
                />
                <Text style={styles.label}>Reason for Visit</Text>
            </View>

            <View style={styles.body}>
                {blocks.map((b, i) => {
                    if (b.kind === "subhead") {
                        return (
                            <Text key={i} style={styles.subhead}>
                                {b.text}
                            </Text>
                        );
                    }
                    if (b.kind === "bullet") {
                        return (
                            <View key={i} style={styles.bulletRow}>
                                <View style={styles.dot} />
                                <Text style={styles.bulletText}>{b.text}</Text>
                            </View>
                        );
                    }
                    return (
                        <Text key={i} style={styles.para}>
                            {b.text}
                        </Text>
                    );
                })}
            </View>
        </View>
    );
}

const styles = StyleSheet.create({
    card: {
        backgroundColor: "#F4FAF8",
        borderWidth: 1,
        borderColor: "#D8ECE7",
        borderRadius: radius.lg,
        paddingVertical: spacing.base,
        paddingHorizontal: spacing.base,
        marginBottom: spacing.base
    },
    header: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.xs,
        marginBottom: spacing.sm
    },
    label: {
        fontFamily: fonts.bold,
        fontSize: 11,
        letterSpacing: 0.7,
        textTransform: "uppercase",
        color: colors.teal600
    },
    body: {
        gap: spacing.sm
    },
    para: {
        fontFamily: fonts.regular,
        fontSize: 14,
        lineHeight: 22,
        color: colors.textPrimary
    },
    subhead: {
        fontFamily: fonts.semiBold,
        fontSize: 12,
        letterSpacing: 0.2,
        color: colors.textSecondary,
        marginTop: spacing.xs
    },
    bulletRow: {
        flexDirection: "row",
        alignItems: "flex-start",
        gap: spacing.sm,
        paddingLeft: spacing.xs
    },
    dot: {
        width: 5,
        height: 5,
        borderRadius: radius.full,
        backgroundColor: colors.teal500,
        marginTop: 8
    },
    bulletText: {
        flex: 1,
        fontFamily: fonts.regular,
        fontSize: 14,
        lineHeight: 21,
        color: colors.textPrimary
    }
});
